/**
 * OnboardingWizard — Dashboard onboarding P2 (2026-07-28).
 *
 * The 4-question onboarding at the heart of the Dashboard "Business OS" tab:
 * one plain question per step (business / customers / data / tasks), Back/Next,
 * and a completion summary. Answers persist to the server on each Next
 * (resumable — a returning tenant lands on their saved step) and the final
 * "Finish setup" marks onboarding complete. State + persistence live in
 * dashboardStore; this component is the presentation + flow.
 *
 * Build fan-out (turning the answers into ingest plans / workflows / sites /
 * agents) is P3+ — the summary here names what comes next without doing it yet.
 *
 * Design doc:
 * docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
 */
import {
  useDashboardStore,
  ONBOARDING_QUESTIONS,
  TOTAL_STEPS,
  type BusinessProfile,
} from '@/stores/dashboardStore';

/**
 * Which Build card each answer unlocks — shown on the summary when an answer is
 * left blank, so a skip is a conscious choice (the card silently hides until the
 * answer exists). Mirrors the per-card guards (BuildActionCard etc.).
 */
const ANSWER_UNLOCKS: Record<keyof BusinessProfile, string> = {
  business: 'Build your website',
  customers: 'Set up your marketing',
  data: 'Build your data',
  tasks: 'Set up your workflows and Put your agents to work',
};

export function OnboardingWizard() {
  const profile = useDashboardStore((s) => s.profile);
  const step = useDashboardStore((s) => s.onboardingStep);
  const isSaving = useDashboardStore((s) => s.isSaving);
  const error = useDashboardStore((s) => s.error);
  const onboardingCompleted = useDashboardStore((s) => s.onboardingCompleted);
  const setAnswer = useDashboardStore((s) => s.setAnswer);
  const setStep = useDashboardStore((s) => s.setStep);
  const saveProfile = useDashboardStore((s) => s.saveProfile);

  // step >= TOTAL_STEPS = past the last question -> the summary view.
  const atSummary = step >= TOTAL_STEPS;

  const goNext = async () => {
    const isLast = step === TOTAL_STEPS - 1;
    setStep(step + 1);
    try {
      await saveProfile(isLast ? { completed: true } : undefined);
    } catch {
      // saveProfile already recorded the error in the store (shown below); the
      // answer stays cached and the user can retry Next.
    }
  };

  const goBack = () => setStep(Math.max(0, step - 1));

  if (atSummary) {
    return (
      <div data-testid="dashboard-onboarding-complete" className="max-w-xl w-full space-y-5">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-vibe-text">
            {onboardingCompleted ? '✓ Your business is set up' : 'Review your answers'}
          </h1>
          <p className="text-sm text-vibe-text-secondary">
            Next, we&apos;ll turn these into your data, workflows, sites, and agents.
          </p>
        </div>
        <dl className="space-y-3">
          {ONBOARDING_QUESTIONS.map((q) => {
            const blank = !profile[q.key].trim();
            return (
              <div key={q.key} className="rounded border border-vibe-border p-3">
                <dt className="text-xs uppercase tracking-wide text-vibe-text-secondary">{q.title}</dt>
                <dd className="mt-1 text-sm text-vibe-text whitespace-pre-wrap">
                  {blank ? (
                    <span
                      data-testid={`onboarding-unlock-${q.key}`}
                      className="text-vibe-text-secondary"
                    >
                      — you skipped this; add it to unlock{' '}
                      <span className="text-vibe-accent">{ANSWER_UNLOCKS[q.key]}</span>.
                    </span>
                  ) : (
                    profile[q.key]
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
        {error && (
          <p data-testid="onboarding-error" className="text-sm text-vibe-error">
            {error}
          </p>
        )}
        <button
          type="button"
          data-testid="onboarding-edit"
          onClick={() => setStep(0)}
          className="px-3 py-1.5 text-sm rounded bg-vibe-bg-tertiary text-vibe-text-secondary hover:text-vibe-text transition-colors"
        >
          Edit answers
        </button>
      </div>
    );
  }

  const q = ONBOARDING_QUESTIONS[step];
  const isLast = step === TOTAL_STEPS - 1;

  return (
    <div data-testid="dashboard-onboarding-wizard" className="max-w-xl w-full space-y-5">
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-vibe-text-secondary">
          Step {step + 1} of {TOTAL_STEPS}
        </p>
        <h1 className="text-xl font-semibold text-vibe-text">{q.title}</h1>
        <p className="text-sm text-vibe-text-secondary">{q.help}</p>
      </div>

      <textarea
        data-testid="onboarding-answer-input"
        aria-label={q.title}
        value={profile[q.key]}
        placeholder={q.placeholder}
        onChange={(e) => setAnswer(q.key, e.target.value)}
        rows={5}
        className="w-full rounded border border-vibe-border bg-vibe-bg-secondary text-vibe-text p-3 text-sm resize-y focus:outline-none focus:border-vibe-accent"
      />

      {error && (
        <p data-testid="onboarding-error" className="text-sm text-vibe-error">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={goBack}
          disabled={step === 0 || isSaving}
          className="px-3 py-1.5 text-sm rounded text-vibe-text-secondary hover:text-vibe-text disabled:opacity-40 transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          onClick={goNext}
          disabled={isSaving}
          className="px-4 py-1.5 text-sm rounded bg-vibe-accent text-white hover:opacity-90 disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Saving…' : isLast ? 'Finish setup' : 'Next'}
        </button>
      </div>
    </div>
  );
}

export default OnboardingWizard;
